'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useNextHydratedStore } from './useNextHydratedStore';
import { createHash } from './createHash';

export type ToggleState = {
  allowNull?: boolean;
  value: boolean | null;
  hash: string | null;
  setHash: (hash: unknown) => void;
  checkHash: (hash: string) => boolean;
  toggle: (value?: boolean) => void;
  setValue: (value: boolean | null) => void;
};

export type PersistHydrationState<S> = S & {
  _hasHydrated: boolean;
  setHasHydrated: (value: boolean) => void;
};

type ToggleStoreOptions = {
  allowNull?: boolean;
  defaultValue?: boolean | null;
  persistStoreKey?: string;
};

export const createToggleStore = (options?: ToggleStoreOptions) => {
  const { defaultValue, persistStoreKey, allowNull = false } = options ?? {};

  const theStore = create<PersistHydrationState<ToggleState>>()(
    persist(
      (set, get) => ({
        allowNull,
        value: allowNull ? defaultValue ?? null : defaultValue ?? false,
        hash: null,
        setHash: (hash: unknown) => set({ hash: createHash(hash) }),
        checkHash: (value: unknown) => get().hash === createHash(value),
        toggle: (on?: boolean) => set((state) => ({ value: on ?? !state.value })),
        setValue: (value: boolean | null) => {
          value = allowNull ? value : value ?? false;
          set({ value });
        },
        _hasHydrated: false,
        setHasHydrated: (value: boolean) => set({ _hasHydrated: value }),
      }),
      {
        name: persistStoreKey ?? '_',
        storage: createJSONStorage(() => localStorage),
        partialize: ({ value, hash }) =>
          persistStoreKey
            ? {
                value,
                hash,
              }
            : {},
        onRehydrateStorage: () => (state) => {
          state?.setHasHydrated(true);
        },
      }
    )
  );

  // --- Immediate client-side hydration ---
  if (typeof window !== 'undefined') {
    // If Next has already hydrated, do it immediately
    if (useNextHydratedStore.getState().value) {
      theStore.persist.rehydrate();
    }

    // Subscribe for future hydration events
    useNextHydratedStore.subscribe(({ value }) => {
      if (value) theStore.persist.rehydrate();
    });
  }

  return theStore;
};
